import { SimpleGrid, Card, CardBody } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';

import {
  FacilitiesTableRowData,
  renderDollarCell,
} from './facilitiesTableOptions';
import { GetBalanceSheetExposureDataQueryGQLType } from './getBalanceSheetExposureData.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';

// imprint (master trust customer) is requesting some custom views
// that we do not plan to support formally at current time.  we have
// elected to do hacky solution for time being.
// NOTE: this implementation should be maintained consistently with sendImprintMasterTrustPortfolioOverviewEmail in credit-facility-lambda
const getImprintMasterTrustDetails = ({
  rows,
  balanceSheetImpactCalcs,
  orgSlug,
}: {
  rows: FacilitiesTableRowData[];
  balanceSheetImpactCalcs: GetBalanceSheetExposureDataQueryGQLType | null;
  orgSlug: string;
}) => {
  if (orgSlug !== 'imprint') {
    return null;
  }

  if (!balanceSheetImpactCalcs) {
    return null;
  }

  const maximumTotalBorrowingBase = Math.max(
    ...rows.map(({ totalBorrowingBase }) => totalBorrowingBase),
  );

  const portfolioGlobalOutstandingBalance = _.sumBy(
    rows,
    ({ globalLoanOutstanding }) => globalLoanOutstanding,
  );

  const masterTrustAvailability =
    maximumTotalBorrowingBase - portfolioGlobalOutstandingBalance;

  const balanceSheetExposure =
    balanceSheetImpactCalcs.balanceSheetImpactCalculations
      .totalBalanceSheetExposure;

  const masterTrustBalanceSheetAvailability =
    masterTrustAvailability + balanceSheetExposure;

  return {
    balanceSheetExposure,
    masterTrustAvailability,
    masterTrustBalanceSheetAvailability,
  };
};

// Fragments
type OverviewTileProps = { children: React.ReactNode; label: React.ReactNode };
const OverviewTile = ({ children, label }: OverviewTileProps) => (
  <Card variant="outline">
    <CardBody display="flex" flexDirection="column" gap="0.5rem">
      <Typography variant="detail-muted">{label}</Typography>
      <Typography variant="base" fontWeight="medium">
        {children}
      </Typography>
    </CardBody>
  </Card>
);

// Exports
type FacilitiesOverviewTilesProps = {
  creditFacilityCount: number;
  totalsRow: FacilitiesTableRowData;
  rows: FacilitiesTableRowData[];
  balanceSheetImpactCalcs: GetBalanceSheetExposureDataQueryGQLType | null;
};

export const FacilitiesOverviewTiles = ({
  creditFacilityCount,
  totalsRow,
  rows,
  balanceSheetImpactCalcs,
}: FacilitiesOverviewTilesProps) => {
  const { user } = useUserAuthOrThrow();

  const masterTrustDetails = useMemo(
    () =>
      getImprintMasterTrustDetails({
        rows,
        balanceSheetImpactCalcs,
        orgSlug: user.organization.slug,
      }),
    [rows, balanceSheetImpactCalcs, user],
  );

  return (
    <SimpleGrid columns={{ sm: 2, md: 4, lg: 4, xl: 4 }} gap="1.5rem">
      {user.userType === 'CAPITAL_PROVIDER' ? (
        <>
          <OverviewTile label={`Total ${user.organization.name} Commitment`}>
            {renderDollarCell(totalsRow.capitalProviderCommitmentAmount)}
          </OverviewTile>
          <OverviewTile label={`Total ${user.organization.name} Outstanding`}>
            {renderDollarCell(totalsRow.capitalProviderLoanOutstanding)}
          </OverviewTile>
          <OverviewTile
            label={`Total ${user.organization.name} Unused Commitment`}
          >
            {renderDollarCell(totalsRow.capitalProviderUnused)}
          </OverviewTile>
        </>
      ) : (
        <>
          <OverviewTile label="Total Commitment">
            {renderDollarCell(totalsRow.globalCommitmentAmount)}
          </OverviewTile>
          <OverviewTile label="Total Outstanding">
            {renderDollarCell(totalsRow.globalLoanOutstanding)}
          </OverviewTile>
          <OverviewTile label="Total Unused Commitment">
            {renderDollarCell(totalsRow.globalUnusedCommitment)}
          </OverviewTile>
        </>
      )}
      <OverviewTile label="Facilities">{creditFacilityCount}</OverviewTile>
      {masterTrustDetails && (
        <>
          <OverviewTile label="Master Trust Availability">
            {renderDollarCell(masterTrustDetails.masterTrustAvailability)}
          </OverviewTile>
          <OverviewTile label="Balance Sheet Expsoure">
            {renderDollarCell(masterTrustDetails.balanceSheetExposure)}
          </OverviewTile>
          <OverviewTile label="Master Trust + Balance Sheet Availability">
            {renderDollarCell(
              masterTrustDetails.masterTrustBalanceSheetAvailability,
            )}
          </OverviewTile>
        </>
      )}
    </SimpleGrid>
  );
};
